import React from "react";
import { Card } from "@/components/ui/card";
import { Check, X, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const FEATURES = [
  {
    category: "Software & Updates",
    rows: [
      { label: "Zugriff auf alle Software-Updates", plus: true, premium: true },
      { label: "Major- und Minor-Upgrades inklusive", plus: true, premium: true },
      { label: "Zugriff auf neue Funktionen bei Erscheinen", plus: true, premium: true },
      { label: "Frühzeitiger Zugriff auf Beta-Programme", plus: false, premium: true },
    ],
  },
  {
    category: "Support & Reaktionszeiten",
    rows: [
      { label: "Zugriff auf Milestone Customer Portal", plus: true, premium: true },
      { label: "Ticket-basierter technischer Support", plus: true, premium: true },
      { label: "Support während Geschäftszeiten (5×8)", plus: true, premium: true },
      { label: "24 / 7 Online-Support", plus: false, premium: true },
      { label: "Garantierte Reaktionszeiten (SLA)", plus: false, premium: true },
      { label: "Priorisierte Fallbearbeitung", plus: false, premium: true },
    ],
  },
  {
    category: "Milestone Learning & Enablement",
    rows: [
      { label: "Milestone Learning Basiszugang", plus: true, premium: true },
      { label: "Aufbauende Selbstlern-Kurse", plus: true, premium: true },
      { label: "Premium-Zugang zu Milestone Learning", plus: false, premium: true },
      { label: "Erweiterte Zertifizierungspfade", plus: false, premium: true },
    ],
  },
  {
    category: "Professional Services",
    rows: [
      { label: "Standard-Zugang zu Milestone Services", plus: true, premium: true },
      { label: "Priorisierter Zugriff auf Consulting-Ressourcen", plus: false, premium: true },
      { label: "Health-Check / System Review Sitzungen", plus: false, premium: true },
    ],
  },
  {
    category: "Kaufmännisches",
    rows: [
      { label: "Preisstaffel nach Laufzeit (1–11 M bis ≥ 60 M)", plus: true, premium: true },
      { label: "Verlängerbar in 1-Monats-Schritten", plus: true, premium: true },
      { label: "Erhältlich für Express+ / Professional+ / Expert / Corporate", plus: true, premium: true },
    ],
  },
];

function Cell({ ok }) {
  return ok ? (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-500">
      <Check className="w-3.5 h-3.5" />
    </span>
  ) : (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-500/10 text-rose-500">
      <X className="w-3.5 h-3.5" />
    </span>
  );
}

export default function CareComparison({ onGoToWizard }) {
  return (
    <div className="space-y-6">
      <Card className="p-5 md:p-6 border-border bg-card card-elev">
        <div className="mb-5">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">
            Milestone Care Pakete
          </div>
          <h2 className="text-2xl md:text-3xl font-bold">Care Plus vs. Care Premium</h2>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Milestone bietet zwei Wartungsstufen an. Beide sichern Ihre XProtect-Investition –
            Care Premium erweitert Care Plus um 24/7-Support, garantierte Reaktionszeiten und Premium-Trainings.
          </p>
        </div>

        {/* Hero cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="rounded-xl border border-border bg-secondary/40 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Standard</div>
                <div className="text-lg font-semibold">Milestone Care Plus</div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Grundlegende Wartung mit voller Update-Berechtigung und Support während Geschäftszeiten.
              Empfehlung für Standard-Installationen ohne 24/7-Anforderungen.
            </p>
            <ul className="space-y-1.5 text-sm">
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>Alle Software-Updates & Upgrades</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>Zugriff auf Customer Portal</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>Support während Geschäftszeiten</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>Milestone Learning Basiszugang</span></li>
            </ul>
          </div>

          <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/15 to-primary/5 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-primary">Premium</div>
                <div className="text-lg font-semibold">Milestone Care Premium</div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Enthält alle Leistungen aus Care Plus und ergänzt sie um 24/7-Support, garantierte
              Reaktionszeiten und priorisierten Zugriff auf Milestone-Ressourcen. Empfehlung für
              unternehmenskritische und KRITIS-Installationen.
            </p>
            <ul className="space-y-1.5 text-sm">
              <li className="flex gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>Alle Leistungen aus Care Plus</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>24 / 7 Online-Support</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>Garantierte Reaktionszeiten (SLA)</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>Premium-Zugang zu Milestone Learning</span></li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>Priorisierter Zugriff auf Professional Services</span></li>
            </ul>
          </div>
        </div>

        {/* Detailed comparison */}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table data-testid="care-comparison-table" className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60 border-b border-border">
                <th className="text-left px-4 py-3 font-semibold w-[58%]">Leistung</th>
                <th className="text-center px-3 py-3 font-semibold">
                  <div className="flex items-center justify-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    Care Plus
                  </div>
                </th>
                <th className="text-center px-3 py-3 font-semibold text-primary">
                  <div className="flex items-center justify-center gap-1.5">
                    <Sparkles className="w-4 h-4" />
                    Care Premium
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((group) => (
                <React.Fragment key={group.category}>
                  <tr>
                    <td colSpan={3} className="bg-secondary/30 border-t border-border px-4 py-2 text-xs uppercase tracking-wider text-primary font-semibold">
                      {group.category}
                    </td>
                  </tr>
                  {group.rows.map((row, idx) => (
                    <tr key={idx} className={cn("border-t border-border", idx % 2 === 0 ? "bg-transparent" : "bg-secondary/10")}>
                      <td className="px-4 py-2.5">{row.label}</td>
                      <td className="px-3 py-2.5 text-center"><Cell ok={row.plus} /></td>
                      <td className="px-3 py-2.5 text-center"><Cell ok={row.premium} /></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground mt-3">
          Angaben nach Milestone Systems – Details und aktuelle Konditionen können abweichen. Alle Pakete werden von Xortec digital ausgeliefert.
        </p>

        {onGoToWizard && (
          <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Nächster Schritt</div>
              <div className="font-semibold text-lg">Passendes Care-Paket direkt in Ihrer Empfehlung wählen</div>
              <div className="text-sm text-muted-foreground">Der Assistent liefert am Ende einen Lizenz-Konfigurator inklusive Care-Auswahl.</div>
            </div>
            <Button onClick={onGoToWizard} className="bg-primary text-primary-foreground">
              Assistent starten <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
